import React, {ReactNode, FC, memo, useRef} from 'react';
import {Popover, Box, Radio, Divider, List, ListItem} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {withStyles} from '@material-ui/core';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import LegendItem from './LegendItem';
import useLegendHandlers from './useLegendHandlers';
import {isTouchDevice} from '../../../utils';
import GameInfo from '../../../../../../game-info/GameInfo';
import {LegendItemProps} from './types';

const CODES = {
    show: 'epic.affiliate.fortnite_games.legends.show',
    hide: 'epic.affiliate.fortnite_games.legends.hide',
    noData: 'epic.affiliate.fortnite_games.legends.no_data'
};

const StyledPopover = withStyles(theme => ({
    root: {
        cursor: 'pointer',
        pointerEvents: isTouchDevice() ? 'all' : 'none'
    },
    paper: {
        width: '100%',
        minWidth: '334px',
        // @ts-ignore
        backgroundColor: theme.palette.background.overlays,
        padding: theme.spacing(2, 0),

        [theme.breakpoints.up('fxs')]: {
            width: 'min-content'
        }
    }
}))(Popover);

const GameInfoContainer = withStyles(theme => ({
    root: {
        '&:not(:last-child)': {
            marginBottom: theme.spacing(2)
        }
    }
}))(Box);

const StyledListItem = withStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        fontWeight: 300,
        [theme.breakpoints.up('xl')]: {
            fontSize: theme.typography.pxToRem(16)
        }
    }
}))(ListItem);

export interface LegendWithTooltipProps {
    label: string;
    value: string | number;
    index: number;

    legendProps: LegendItemProps;
    endAdornment?: ReactNode;
    isTooltip?: boolean;

    onFocus: (key: string | number) => void;
    onBlur: (key: string | number) => void;
    onToggleVisibility: (key: string | number, isHidden: boolean) => void;
    getMessage?: (code: string) => string;
}

const LegendWithTooltip: FC<LegendWithTooltipProps> = ({
    value,
    label,
    legendProps,
    endAdornment,
    isTooltip = true,
    onFocus,
    onBlur,
    onToggleVisibility,
    getMessage
}: LegendWithTooltipProps) => {
    const {isHidden, disabled, isEmpty} = legendProps;
    const isDisabled = isHidden || disabled;

    const anchorElRef = useRef(null);

    const {isTooltipOpen, legendHandlers, handleClose, handleToggleVisibility} = useLegendHandlers({
        value,
        legendProps,
        onFocus,
        onBlur,
        onToggleVisibility
    });

    const code = String(value);
    const isIslandCode = /^\d+$/.test(code.split('-').join(''));
    const islandCode = isIslandCode ? code : undefined;

    return (
        <>
            <Box display="inline-flex" {...legendHandlers} ref={anchorElRef}>
                <LegendItem {...legendProps} />
                {endAdornment}
            </Box>
            {isTooltip && (
                <StyledPopover
                    open={isTooltipOpen}
                    disableRestoreFocus
                    anchorEl={anchorElRef.current}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    onClose={handleClose}>
                    <GameInfoContainer mx={4}>
                        <GameInfo title={label} code={islandCode} />
                    </GameInfoContainer>
                    {isEmpty && (
                        <Box mx={4}>
                            <Alert severity="warning" variant="standard">
                                {getMessage(CODES.noData)}
                            </Alert>
                        </Box>
                    )}
                    {isTouchDevice() && !isEmpty && (
                        <>
                            <Divider light />
                            <List>
                                <StyledListItem
                                    button
                                    disabled={!isHidden}
                                    onClick={handleToggleVisibility}>
                                    <Radio checked={!isHidden} />
                                    {getMessage(CODES.show)}
                                </StyledListItem>
                                <StyledListItem
                                    button
                                    disabled={isDisabled}
                                    onClick={handleToggleVisibility}>
                                    <Radio checked={isDisabled} />
                                    {getMessage(CODES.hide)}
                                </StyledListItem>
                            </List>
                        </>
                    )}
                </StyledPopover>
            )}
        </>
    );
};

export default memo(withLocalization(LegendWithTooltip));
